<script setup lang="ts">

	import {computed} from 'vue';
	import {
		addOrderLineToShoppingCart,
		getRoundedValue,
		ignoreKeyPressIfNotInteger,
		updateOrderLineAmount
	} from 'dummen-shared';
	import type {ShoppingCartOrderLine} from 'dummen-shared/src/interfaces/order.line.type.ts';
	import {useShoppingCartStore} from '@/store/shoppingcart.store.ts';

	const props = defineProps<{
		week: number;
		year: number;
		substitute: ShoppingCartOrderLine;
	}>();

	const shoppingCartStore = useShoppingCartStore();

	/**
	 * Returns the order line in the shopping cart that matches the current order line.
	 */
	const shoppingCartOrderLine = computed(() => {
		return shoppingCartStore.getOrderLinesForWeek(props.week, props.year)?.find((line: ShoppingCartOrderLine) => {
			return line &&
				line.articleId === props.substitute.articleId &&
				line.formId === props.substitute.formId &&
				line.week === props.week &&
				line.year === props.year
		})
	});

	const amountAvailable = computed(() => Math.max(props.substitute.amountAvailable, 0));

	const maxOrderAmount = computed(() => amountAvailable.value);

	const amount = computed({
		get() {
			return shoppingCartOrderLine.value?.amount ?? 0;
		},

		set(newAmount) {
			// If the order line should be created or updated, first check if the desired amount
			// is a valid integer. If not return, so that the input will reset to the last valid amount.
			if (newAmount === null || newAmount === undefined || !Number.isInteger(newAmount)) {
				return;
			}

			const existingOrderLine = shoppingCartOrderLine.value;

			// At this point, the existing order line should be updated.
			if (existingOrderLine) {
				updateOrderLineAmount(existingOrderLine, newAmount);
				return;
			}

			addOrderLineToShoppingCart({
				...props.substitute,
				amount: newAmount
			});
		}
	});

	const setRoundedValue = (event: FocusEvent) => {
		const el = event.target as HTMLInputElement;
		if (!el.value) {
			amount.value = 0;
			return;
		}

		amount.value = getRoundedValue(
			Number.parseInt(el.value),
			amountAvailable.value,
			props.substitute.packingUnit,
			false
		);
	};

</script>

<template>
	<form novalidate onsubmit="return false;">
		<input v-model="amount"
		       :max="maxOrderAmount"
		       :step="substitute.packingUnit"
		       class="orderinput"
		       min="0"
		       type="number"
		       @blur="setRoundedValue"
		       @keypress="ignoreKeyPressIfNotInteger($event)" />

		<span>{{ substitute.amountAvailable }}</span>
	</form>
</template>
